<template lang="pug">
  modal(:title='modalTitle' @closeModal='closeModal' maxWidth='550px')
    .container.relative
      .trial.row.middle.center(v-if='isTrial')
        .row.middle.center.m-bottom-8
          simple-svg.dark-gray-icon.m-right-1(:filepath='lockIcon' width='20px' height='20px')
          h2.text-normal.dark-gray {{ $t('trial.disabledOnTrial') }}
      .column(v-if='success')
        .column.middle.center
          simple-svg(:filepath='checkIcon' width='42px' height='42px')
          h2.text-center.title-medium.m-top-2 {{ $t('krea.publishContentModal.success') }}
      .column.m-top-2(v-else)
        .langs.m-bottom-2
          .width-100.row.middle(v-for='(percentage, locale) in languagesAvailable' :key='locale')
            checkbox.m-right-1(
              :updateCheck='() => selectLang(locale)'
              :disabled='percentage < 100'
              :checked='isLangSelected(locale)'
            )
            .item-1.row.middle
              simple-svg(:filepath='getLangIcon(locale)' width='20px' height='20px')
              h2.m-left-1.text-normal {{ getLangLabel(locale) }}
              h2.text-normal.dark-gray.m-left-auto {{ $t('krea.publishContentModal.completePercentage', {percentage}) }}
            .item-1.row.middle.text-right
              h2.item-1.text-normal(v-if='percentage === 100') {{ $t('krea.publishContentModal.readyToPublish') }}
              router-link.item-1.blue.underline.text-normal(
                :to='{name: routes.kreaEducationalContent, params: {id: modalProps.contentId}, query: {locale}}'
                @click.native='closeModal'
                v-else
              ) {{ $t('krea.publishContentModal.completeToPublish') }}
        tag-selector(
          :label='$t("krea.publishContentModal.keywordsLabel")'
          name='keywords'
          tagClass='keyword'
          :title='$t("krea.publishContentModal.keywordsPlaceholder", {keywordsAmount: 10 * (completedLanguages.length || 1)})'
          :placeholder='$t("krea.publishContentModal.keywordsPlaceholder", {keywordsAmount: 10 * (completedLanguages.length || 1)})'
          :tags='keywords'
          :updateMethod='editTags'
          :maxTags='30'
          maxHeight='150px'
        )
        .m-top-1.row.middle(v-if='showError')
          simple-svg.red-icon.m-right-05.red-stroke-icon(:filepath='errorIcon' width='16px' height='16px')
          h2.text-normal.red {{ $t("krea.publishContentModal.error") }}
        .row.middle.end.m-top-2
          button.secondary-button.m-right-2(@click='closeModal') {{ $t("krea.publishContentModal.cancel") }}
          button.primary-button(:disabled='!selectedLanguages.length' @click='submit') {{ $t('krea.publishContentModal.publish') }}
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Modal from '@/components/Modal'
import TagSelector from '@/components/Inputs/TagSelector'
import { routes } from '../../router'
import KSelect from '@/components/KSelect'
import checkIcon from '@/assets/ic_check.svg'
import Checkbox from '@/components/Inputs/Checkbox'
import { educationalProgramLanguageStatus } from '../../utils/constants'
import errorIcon from '@/assets/ic_stop.svg'
import { mapLanguageOptions, supportedLanguages } from '../../constants/languages'
import { i18n } from '../../main'
import userRole from '@/mixins/userRole'
import lockIcon from '@/assets/ic_lock.svg'
import { deepCopy } from '../../utils/object'

export default {
  mixins: [userRole],
  components: { Modal, TagSelector, KSelect, Checkbox },
  data() {
    return {
      errorIcon,
      lockIcon,
      checkIcon,
      routes,
      keywords: [],
      success: false,
      showError: false,
      selectedLanguages: []
    }
  },
  mounted() {
    this.keywords = this.modalProps.keywords || []
  },
  computed: {
    ...mapState({
      modalProps: state => state.behavior.kreaPublishContentModal.props
    }),
    modalTitle() {
      if (this.success) return null
      else return i18n.t('krea.publishContentModal.publishTitle')
    },
    languagesOptions() {
      if (this.keywordsEditionMode) return null
      return supportedLanguages
        .filter(lang => this.modalProps.languages.includes(lang.lang))
        .map(mapLanguageOptions)
    },
    completedLanguages() {
      return Object.keys(this.modalProps.completeness).filter(key => this.modalProps.completeness[key] === 100)
    },
    languagesAvailable() {
      const approved = Object
        .keys(this.modalProps.completeness)
        .filter(key => !this.modalProps.languages.includes(key))
      const languages = deepCopy(this.modalProps.completeness)
      for (const lang of approved) delete languages[lang]
      return languages
    }
  },
  methods: {
    ...mapActions([
      'hideElement',
      'setKreaEducationalContentKeywords',
      'setKreaEducationalContentStatus'
    ]),
    isLangSelected(lang) {
      return this.selectedLanguages.includes(lang)
    },
    selectLang(lang) {
      if (this.isLangSelected(lang)) {
        this.selectedLanguages = this.selectedLanguages.filter(locale => locale !== lang)
      } else {
        this.selectedLanguages = [...this.selectedLanguages, lang]
      }
    },
    getLangIcon(lang) {
      return supportedLanguages.find(language => language.lang === lang).icon
    },
    getLangLabel(lang) {
      return supportedLanguages.find(language => language.lang === lang).label
    },
    closeModal() {
      this.hideElement({ element: 'kreaPublishContentModal' })
      if (this.success) this.$router.push({ name: routes.kreaPublished })
    },
    editTags(newValue) {
      this.keywords = newValue
    },
    async submit() {
      this.showError = false
      await this.setKreaEducationalContentKeywords({ id: this.modalProps.contentId, keywords: this.keywords })
      const { success, error } = await this.setKreaEducationalContentStatus({
        id: this.modalProps.contentId,
        status: educationalProgramLanguageStatus.approved,
        locales: this.selectedLanguages
      })
      if (success) {
        this.success = true
      } else {
        this.showError = true
        this.$notify({
          group: 'custom-toast',
          duration: 5000,
          type: 'error',
          text: error
        })
      }

      this.$emit('success')
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.modal{
  --max-width: 640px;
}

.container{
  .trial{
    background-color: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(1px);
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 1;
  }
}

.langs{
  &>*:not(:last-child){
    margin-bottom: 15px;
  }
}

.ti-tag{
  &.keyword{
    background-color: $blue !important;
    span{
      color: $white;
    }
    .ti-icon-close {
      color: $white;
      margin-left: 5px;
    }
  }
}
</style>
